import { Button, Form, Input, Row, Col } from 'antd';
import {
  EyeTwoTone,
  EyeInvisibleOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { openNotification } from '../../utils';
import axios from 'axios';
import type { Rule } from "antd/es/form";
import sahaLogo from '../../assets/logo-saha.svg';
import { getCourseId, savePendingCourseId } from '../../auth/courseRedirect';
import './Register.scss';

type Step = {
  label: string;
  title: string;
  name: string;
  placeholder: string;
  rules: Rule[];
  type: string;
};

const Register = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formValues, setFormValues] = useState<Record<string, string>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const formRef = useRef<any>(null);

  // Extract course ID from the URL
  const searchParams = new URLSearchParams(location.search);
  const affiliate: any = searchParams.get('affiliate');
  const courseId = getCourseId(searchParams.get('course'));
  if (affiliate) localStorage.setItem('affiliate', affiliate);

  // Steps tanımı
  const steps: Step[] = [
    {
      label: 'Ad',
      title: 'Merhaba! İsmini öğrenebilir miyim? 👋',
      name: 'firstName',
      placeholder: 'Adını buraya yazabilirsin',
      rules: [{ required: true, message: 'Adını yazman lazım 😅' }],
      type: 'text',
    },
    {
      label: 'Soyad',
      title: `${formValues.firstName ? `${formValues.firstName},` : ''} bir de soyadını alalım mı?`,
      name: 'lastName',
      placeholder: 'Soyadın buraya',
      rules: [{ required: true, message: 'Soyadını da unutma! 🙈' }],
      type: 'text',
    },
    {
      label: 'E-posta',
      title: `E-posta adresini yazar mısın? (Söz, spam yok! 🙃)`,
      name: 'email',
      placeholder: 'ornek@eposta.com',
      rules: [
        { required: true, message: 'E-posta önemli, burayı atlama! 😅' },
        { type: 'email' as const, message: 'Burası e-posta olmalı, örn: isim@site.com' },
      ],
      type: 'text',
    },
    {
      label: 'Telefon',
      title: 'Telefon numaranı yazmak ister misin? (Zorunlu değil, dileyenler için 😎)',
      name: 'phone',
      placeholder: '5XXXXXXXXX (isteğe bağlı)',
      rules: [
        { pattern: /^$|^\d{10}$/, message: 'Telefon 10 haneli olmalı ya da boş bırak!' },
      ],
      type: 'text',
    },
    {
      label: 'Şifre',
      title: 'Gizli şifreni seç bakalım! (Kimseye söyleme 😄)',
      name: 'password',
      placeholder: 'Bir şifre seç',
      rules: [
        { required: true, message: 'Şifreyi boş bırakma!' },
        {
          validator: (_: any, value: any) => {
            const errors: string[] = [];
            if (!value) return Promise.reject(new Error('Şifre boş olamaz!'));
            if (value.length < 8) errors.push('En az 8 karakter olmalı!');
            if (!value.match(/[A-Z]/)) errors.push('Bir tane büyük harf olsun!');
            if (!value.match(/[a-z]/)) errors.push('Bir tane küçük harf olsun!');
            if (!value.match(/[0-9]/)) errors.push('Bir rakam da lazım!');
            if (errors.length > 0) return Promise.reject(new Error(errors.join(' ')));
            return Promise.resolve();
          },
        },
      ],
      type: 'password',
    },
    {
      label: 'Şifre Tekrar',
      title: 'Şifreni tekrar gir ki, her şey yolunda gitsin! 😊',
      name: 'confirmPassword',
      placeholder: 'Şifreni tekrar yaz',
      rules: [
        { required: true, message: 'Şifreyi tekrar yazmalısın!' },
        {
          validator: (_: any, value: any) => {
            if (!value || formValues['password'] === value) return Promise.resolve();
            return Promise.reject('Şifreler aynı olmalı 🤷‍♂️');
          }
        }
      ],
      type: 'password',
    },
  ];

  const summaryStep = steps.length;
  const isSummary = currentStep === summaryStep;
  const currentStepConfig = !isSummary ? steps[currentStep] : undefined;

  const handleNext = async (name: string, value: string) => {
    try {
      await formRef.current.validateFields([name]);
      setFormValues((prev) => ({ ...prev, [name]: value }));
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else if (currentStep === steps.length - 1) {
        setCurrentStep(summaryStep);
      }
    } catch {
      // antd error gösterimini kendi yapıyor
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleEdit = (index: number) => {
    setCurrentStep(index);
    setTimeout(() => {
      if (formRef.current) {
        const name = steps[index].name;
        formRef.current.setFieldsValue({
          [name]: formValues[name]
        });
      }
    }, 0);
  };

  const onFinish = () => {
    setLoading(true);

    const userData = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email,
      password: formValues.password,
      // telefon boşsa null, doluysa olduğu gibi gönder
      phone: formValues.phone === "" ? null : formValues.phone,
      courseId,
    };

    const apiUrl = process.env.REACT_APP_API_BASE_URL + '/auth/register';

    axios
      .post(apiUrl, userData)
      .then(() => {
        savePendingCourseId(courseId);
        navigate(`/email-verify-check?email=${encodeURIComponent(formValues.email)}`, { replace: true });
      })
      .catch((e: any) => {
        if (e?.response?.data?.errorCode === 'account-already-exists') {
          openNotification('error', 'Hmm?', 'Bu e-posta zaten kayıtlı gibi 🤔');
        } else {
          openNotification('error', 'Bir hata oldu', 'Kaydın alınamadı 😓');
        }
        setLoading(false);
      });
  };


  // Şifreler aynı mı kontrolü
  const canGoNext =
    currentStepConfig?.name === 'confirmPassword'
      ? !!formValues['confirmPassword'] &&
      formValues['password'] === formValues['confirmPassword']
      : currentStepConfig?.name !== 'phone'
        ? !!formValues[currentStepConfig?.name || ""]
        : true;

  return (
    <div className="register-page">
      <div className="register-card">
        {/* Adım adım ekran */}
        {!isSummary && (
          <>
            <div style={{ textAlign: 'center' }} >
              <img src={sahaLogo} alt="Saha Eğitim" style={{ width: 300, maxWidth: "100%", marginBottom: 16, marginTop: 16 }} className="register-logo" />
            <h2 style={{ color: '#bd42ab', marginBottom: 24 }}>Kayıt</h2>
            </div>
            <div className="register-step-row">
              <div className="register-ai-speech">
                <span>{currentStepConfig?.title}</span>
              </div>
            </div>
          </>
        )}

        <Form
          ref={formRef}
          layout="vertical"
          initialValues={formValues}
          style={{ margin: 0 }}
        >
          {/* Sadece aktif input */}
          {!isSummary && currentStepConfig && (
            <>
              <Form.Item
                name={currentStepConfig.name}
                rules={currentStepConfig.rules}
                initialValue={formValues[currentStepConfig.name]}
              >
                {currentStepConfig.type === 'password' ? (
                  <Input.Password
                    size="large"
                    className="form-input"
                    placeholder={currentStepConfig.placeholder}
                    onChange={e =>
                      setFormValues((prev) => ({
                        ...prev,
                        [currentStepConfig.name]: e.target.value,
                      }))
                    }
                    onPressEnter={e => handleNext(currentStepConfig.name, e.currentTarget.value)}
                    iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    value={formValues[currentStepConfig.name]}
                    autoFocus
                  />
                ) : (
                  <Input
                    size="large"
                    className="form-input"
                    placeholder={currentStepConfig.placeholder}
                    onChange={e =>
                      setFormValues((prev) => ({
                        ...prev,
                        [currentStepConfig.name]: e.target.value,
                      }))
                    }
                    onPressEnter={e => handleNext(currentStepConfig.name, e.currentTarget.value)}
                    value={formValues[currentStepConfig.name]}
                    autoFocus
                  />
                )}
              </Form.Item>
              <div className="register-nav-btns">
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  size="middle"
                  className="register-back-btn"
                />
                <Button
                  icon={<ArrowRightOutlined />}
                  onClick={() =>
                    handleNext(
                      currentStepConfig.name,
                      formValues[currentStepConfig.name]
                    )
                  }
                  size="middle"
                  type="primary"
                  className="register-next-btn"
                />
              </div>
            </>
          )}
        </Form>

        {/* Summary ekranı */}
        {isSummary && (
          <>
            <div className="register-summary">
              <div className="register-summary-logo-row">
                <img src={sahaLogo} alt="Saha Eğitim" style={{ width: 300, marginBottom: 24, marginTop: 16 }} className="register-logo" />
              </div>
              <div className="register-summary-title">Bak bakalım her şey doğru mu? 😇</div>
              <div className="register-summary-list">
                {steps.map((step, i) => (
                  <div className="register-summary-item" key={step.name}>
                    <span className="register-summary-label">{step.label}:</span>
                    <span className="register-summary-value">
                      {step.type === 'password'
                        ? '••••••••'
                        : formValues[step.name]}
                    </span>
                    <Button
                      icon={<EditOutlined />}
                      shape="circle"
                      size="small"
                      className="register-edit-btn"
                      onClick={() => handleEdit(i)}
                    />
                  </div>
                ))}
              </div>
            </div>
            <Button
              loading={loading}
              className="register-btn"
              type="primary"
              size="large"
              block
              onClick={onFinish}
            >
              Kayıt ol ve aramıza katıl 🎉
            </Button>
          </>
        )}

        <Row className="register-footer-links mt-24 " justify={'center'}>
          <Col>
            <p>
              Hesabın var mı?{' '}
              <Link className="" to={'/giris'}>
                Giriş Yap
              </Link>
            </p>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Register;
